import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import './Button.css';

/**
 * Colour convention, inherited from aximedic.com and applied consistently here:
 *   honey  = "sign up" / primary consumer action
 *   teal   = "talk to us" / secondary
 * Contrast is measured, not eyeballed:
 *   honey  — #0b0b0b on #F5B301  = 9.27:1  (AA at any size)
 *   teal   — #ffffff on #0891B2  = 4.54:1  (AA normal text)
 *   ghost  — #0b0b0b on #ffffff  = 17.43:1
 */
type Variant = 'honey' | 'teal' | 'ghost';
type Size = 'md' | 'lg';

type ButtonProps<T extends ElementType> = {
  as?: T;
  variant?: Variant;
  size?: Size;
  block?: boolean;
  className?: string;
  children: ReactNode;
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children' | 'className'>;

export function Button<T extends ElementType = 'button'>({
  as,
  variant = 'honey',
  size = 'md',
  block = false,
  className = '',
  children,
  ...rest
}: ButtonProps<T>) {
  // `as` is erased to a plain ElementType here: TypeScript cannot prove that a
  // className/children pair is valid for an arbitrary T, so the props are
  // declared explicitly above and only the caller-facing surface stays generic.
  const Tag = (as ?? 'button') as ElementType;
  const classes = [
    'btn',
    `btn--${variant}`,
    `btn--${size}`,
    block ? 'btn--block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Tag className={classes} {...rest}>
      {children}
    </Tag>
  );
}
